<template>
  <div class="kb-rows">
    <!-- Loading, no Auth record, or the lookup failed -->
    <div
      v-if="loading || notFound || error"
      class="kb-row"
    >
      <div class="min-w-0">
        <h4 class="kb-row-title">
          Authentication status
        </h4>
        <p
          v-if="loading"
          class="kb-row-hint"
        >
          <KbSpinner class="mr-1.5" />
          Loading sign-in details…
        </p>
        <p
          v-else-if="notFound"
          class="kb-row-hint"
        >
          No Firebase Auth record found for this user.
        </p>
        <p
          v-else
          class="kb-row-hint"
        >
          <span class="text-kb-alert">{{ error }}</span>
        </p>
      </div>
    </div>

    <template v-else-if="authInfo">
      <!-- Login methods, with when the account was created and last used -->
      <div class="kb-row">
        <div class="min-w-0">
          <h4 class="kb-row-title">
            Login method
          </h4>
          <p class="kb-row-meta mt-0.5 mb-0">
            Created {{ formatDate(authInfo.createdAt) }}<template v-if="authInfo.lastSignInAt">
              · last sign in {{ formatDate(authInfo.lastSignInAt) }}
            </template>
          </p>
        </div>
        <div class="kb-row-aside">
          <span
            v-for="method in authInfo.loginMethods"
            :key="method"
            class="kb-tag kb-tag-neutral"
          >{{ method }}</span>
        </div>
      </div>

      <!-- Email verification, for Email/Password users -->
      <div
        v-if="authInfo.loginMethods.includes('Email/Password')"
        class="kb-row"
      >
        <h4 class="kb-row-title">
          Email
        </h4>

        <!-- Actions for pending users with unverified email -->
        <div class="kb-row-aside">
          <span
            class="kb-tag"
            :class="authInfo.emailVerified ? 'kb-tag-ok' : 'kb-tag-warn'"
          >{{ authInfo.emailVerified ? 'Verified' : 'Not verified' }}</span>
          <template v-if="member.member_type === 'pending' && !authInfo.emailVerified">
            <Button
              label="Resend link"
              icon="pi pi-send"
              text
              severity="secondary"
              size="small"
              :loading="resendLoading"
              @click="resendVerification"
            />
            <Button
              label="Verify"
              icon="pi pi-check"
              outlined
              severity="secondary"
              size="small"
              :loading="verifyLoading"
              @click="confirmVerifyEmail"
            />
          </template>
        </div>
      </div>

      <!-- Account Disabled Status (only show if disabled) -->
      <div
        v-if="authInfo.disabled"
        class="kb-row"
      >
        <h4 class="kb-row-title">
          Account
        </h4>
        <div class="kb-row-aside">
          <span class="kb-tag kb-tag-alert">Disabled</span>
        </div>
      </div>

      <!-- Two-factor authentication -->
      <div class="kb-row">
        <div class="min-w-0">
          <h4 class="kb-row-title">
            Two-factor authentication
          </h4>
          <ul
            v-if="authInfo.enrolledFactors && authInfo.enrolledFactors.length > 0"
            class="kb-row-meta m-0 mt-0.5 list-none p-0"
          >
            <li
              v-for="f in authInfo.enrolledFactors"
              :key="f.uid"
            >
              {{ f.displayName || 'SMS' }}
              <span v-if="f.phoneNumber">· {{ f.phoneNumber }}</span>
            </li>
          </ul>
          <p
            v-else
            class="kb-row-hint"
          >
            Not enabled
          </p>
        </div>
        <div class="kb-row-aside">
          <span
            v-if="authInfo.enrolledFactors && authInfo.enrolledFactors.length > 0"
            class="kb-tag kb-tag-ok"
          >Enabled</span>
          <template v-if="authInfo.enrolledFactors && authInfo.enrolledFactors.length > 0">
            <Button
              label="Remove all"
              icon="pi pi-shield"
              severity="danger"
              text
              size="small"
              :loading="unenrollMfaLoading"
              @click="confirmUnenrollMfa"
            />
          </template>
        </div>
      </div>

      <!-- Password reset for email/password users -->
      <div
        v-if="authInfo.loginMethods.includes('Email/Password')"
        class="kb-row"
      >
        <div>
          <h4 class="kb-row-title">
            Password
          </h4>
          <p class="kb-row-hint">
            Emails the member a link to set a new password
          </p>
        </div>
        <div class="kb-row-aside">
          <Button
            label="Send reset link"
            icon="pi pi-key"
            outlined
            severity="secondary"
            size="small"
            :loading="resetPasswordLoading"
            @click="sendPasswordReset"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import type { Member } from '~/shared/types/member'

interface AuthInfo {
  uid: string
  email: string
  emailVerified: boolean
  loginMethods: string[]
  providers: Array<{
    providerId: string
    uid: string
    email: string
  }>
  enrolledFactors?: Array<{
    uid: string
    displayName?: string
    factorId: string
    phoneNumber?: string
    enrollmentTime?: string
  }>
  disabled: boolean
  createdAt: string
  lastSignInAt?: string
}

interface Props {
  member: Member
}

const props = defineProps<Props>()
const toast = useToast()
const confirm = useConfirm()

const loading = ref(true)
const error = ref<string | null>(null)
const notFound = ref(false)
const authInfo = ref<AuthInfo | null>(null)
const resendLoading = ref(false)
const verifyLoading = ref(false)
const resetPasswordLoading = ref(false)
const unenrollMfaLoading = ref(false)

async function loadAuthInfo() {
  loading.value = true
  error.value = null
  notFound.value = false

  try {
    const data = await $fetch<AuthInfo>('/api/admin/auth/user-info', {
      query: { uid: props.member.id },
    })
    authInfo.value = data
  }
  catch (err: unknown) {
    const fetchError = err as { statusCode?: number }
    if (fetchError.statusCode === 404) {
      notFound.value = true
    }
    else {
      console.error('Error loading auth info:', err)
      error.value = err instanceof Error ? err.message : 'Failed to load authentication info'
    }
  }
  finally {
    loading.value = false
  }
}

async function resendVerification() {
  resendLoading.value = true

  try {
    const result = await $fetch<{ success: boolean, link: string, message: string }>('/api/admin/auth/resend-verification', {
      method: 'POST',
      body: { uid: props.member.id },
    })

    if (result.success) {
      toast.add({
        severity: 'success',
        summary: 'Verification Link Generated',
        detail: 'Copy the link below and send it to the user',
        life: 10000,
      })

      // Show the link in a copyable format
      confirm.require({
        message: result.link,
        header: 'Verification Link',
        icon: 'pi pi-link',
        acceptLabel: 'Copy Link',
        rejectLabel: 'Close',
        accept: () => {
          navigator.clipboard.writeText(result.link)
          toast.add({
            severity: 'success',
            summary: 'Copied',
            detail: 'Verification link copied to clipboard',
            life: 3000,
          })
        },
      })
    }
  }
  catch (err) {
    console.error('Error resending verification:', err)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err instanceof Error ? err.message : 'Failed to generate verification link',
      life: 5000,
    })
  }
  finally {
    resendLoading.value = false
  }
}

function confirmVerifyEmail() {
  confirm.require({
    message: `Mark ${props.member.email} as verified? This will allow them to complete signup without email verification.`,
    header: 'Verify Email Address',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Verify',
    rejectLabel: 'Cancel',
    accept: async () => {
      await verifyEmail()
    },
  })
}

async function verifyEmail() {
  verifyLoading.value = true

  try {
    const result = await $fetch<{ success: boolean, message: string }>('/api/admin/auth/verify-email', {
      method: 'POST',
      body: { uid: props.member.id },
    })

    if (result.success) {
      toast.add({
        severity: 'success',
        summary: 'Email Verified',
        detail: `${props.member.email} has been marked as verified`,
        life: 5000,
      })

      // Reload auth info
      await loadAuthInfo()
    }
  }
  catch (err) {
    console.error('Error verifying email:', err)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err instanceof Error ? err.message : 'Failed to verify email',
      life: 5000,
    })
  }
  finally {
    verifyLoading.value = false
  }
}

async function sendPasswordReset() {
  resetPasswordLoading.value = true

  try {
    const result = await $fetch<{ success: boolean, link: string, message: string }>('/api/admin/auth/password-reset-link', {
      method: 'POST',
      body: { uid: props.member.id },
    })

    if (result.success) {
      toast.add({
        severity: 'success',
        summary: 'Password Reset Link Generated',
        detail: 'Copy the link below and send it to the user',
        life: 10000,
      })

      // Show the link in a copyable format
      confirm.require({
        message: result.link,
        header: 'Password Reset Link',
        icon: 'pi pi-link',
        acceptLabel: 'Copy Link',
        rejectLabel: 'Close',
        accept: () => {
          navigator.clipboard.writeText(result.link)
          toast.add({
            severity: 'success',
            summary: 'Copied',
            detail: 'Password reset link copied to clipboard',
            life: 3000,
          })
        },
      })
    }
  }
  catch (err) {
    console.error('Error generating password reset link:', err)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err instanceof Error ? err.message : 'Failed to generate password reset link',
      life: 5000,
    })
  }
  finally {
    resetPasswordLoading.value = false
  }
}

function formatDate(dateString: string | undefined): string {
  if (!dateString) return 'N/A'

  try {
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }
  catch {
    return 'Invalid date'
  }
}

function confirmUnenrollMfa() {
  confirm.require({
    message: `Remove all second factors for ${props.member.email}? They will be able to sign in with only their password until they re-enroll.`,
    header: 'Remove Two-Factor Authentication',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Remove',
    rejectLabel: 'Cancel',
    acceptClass: 'p-button-danger',
    accept: async () => {
      await unenrollMfa()
    },
  })
}

async function unenrollMfa() {
  unenrollMfaLoading.value = true

  try {
    const result = await $fetch<{ success: boolean, message: string }>('/api/admin/auth/unenroll-mfa', {
      method: 'POST',
      body: { uid: props.member.id },
    })

    if (result.success) {
      toast.add({
        severity: 'success',
        summary: 'Two-factor removed',
        detail: `Second factors cleared for ${props.member.email}`,
        life: 5000,
      })
      await loadAuthInfo()
    }
  }
  catch (err) {
    console.error('Error unenrolling MFA:', err)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err instanceof Error ? err.message : 'Failed to remove second factors',
      life: 5000,
    })
  }
  finally {
    unenrollMfaLoading.value = false
  }
}

onMounted(() => {
  loadAuthInfo()
})
</script>
